import React, {useState} from 'react'
import {Stack, StackProps, useMultiStyleConfig, FlexProps, TextProps} from '@chakra-ui/react'
import {Loader} from '_uiComponents'
import {useIntl} from 'react-intl'
import {usePageDesigner, useAnalytics, useViewport} from '_customHooks'
import {Page, EventNewsletterLabel, ResponseSavePreference, ResponseStep1} from '_types'
import {useModal} from '_context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {NewsletterRegistrationStep1} from './partials/newsletter-step-1'
import {NewsletterRegistrationStep2} from './partials/newsletter-step-2'
import {NewsletterRegistrationStep3} from './partials/newsletter-step-3'
import {hashValueHex} from '_analytics/utils/generic-utils'

enum NewsletterStep {
    'STEP_1' = 0,
    'STEP_2' = 1,
    'STEP_3' = 2,
}

interface NewsletterModalProps {
    emailInputFooter?: string
    analyticsLabel: EventNewsletterLabel
}

/**
 * @name NewsletterModal
 *
 * @component
 *
 * @description
 * Renders the body of Newsletter modal.
 *
 * @remarks
 * This modal has 3 steps.
 *
 * @param {NewsletterModalProps} props - Properties object
 * @param {string} props.emailInputFooter - Email from footer input
 * @param {EventNewsletterLabel} props.analyticsLabel - Newsletter analytics labels type.
 *
 * @returns {React.FC<NewsletterModalProps>} Rendered `NewsletterModal` component.
 */
const NewsletterModal: React.FC<NewsletterModalProps> = ({analyticsLabel, emailInputFooter}) => {
    const {isMobile} = useViewport()
    const styles = useMultiStyleConfig('NewsletterModal') as Record<string, StackProps & TextProps & FlexProps>
    const {analyticsEventNewsletter} = useAnalytics()
    const {formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const {updateModalImage, updateModalTitle} = useModal()

    const {pageComponents} = usePageDesigner({pageId: 'overlay-newsletter'})
    const {bannerNewsletter} = mapPageDesignerData(pageComponents)

    const titleStep2 = formatMessage({id: 'label.newsletter.preferences.title', defaultMessage: 'Communications tailored just for you.'})
    const titleStep3 = formatMessage({id: 'label.newsletter.form.thanks', defaultMessage: 'Thank you.'})

    if (bannerNewsletter?.image_url_desktop) {
        updateModalImage(bannerNewsletter?.image_url_desktop?.url, bannerNewsletter?.image_url_desktop?.alt)
    }

    const [step, setStep] = useState<NewsletterStep>(NewsletterStep.STEP_1)
    const [email, setEmail] = useState('')
    const [isLoading, setLoading] = useState(false)

    const handleStep1Submit = (data: ResponseStep1) => {
        analyticsEventNewsletter({action: 'submit_ok', label: analyticsLabel, userScore: data.userScore})
        setEmail(data.email)
        setStep(NewsletterStep.STEP_2)
        analyticsEventNewsletter({action: 'step2', label: analyticsLabel})
        updateModalTitle(formatMessage({id: 'label.newsletter.preferences.banner', defaultMessage: 'Your preferences'}))
    }

    const handleStep2Submit = (data: ResponseSavePreference) => {
        analyticsEventNewsletter({action: 'submit_ok_step2', label: analyticsLabel})
        analyticsEventNewsletter({
            action: 'save_preferences',
            label: `${data.name}|${data.surname}|${data.consent}|${data.interest}`,
            preferences: data.interestlist.replaceAll(';', '|'),
            hashId: hashValueHex(data.email.toLowerCase()),
        })
        setStep(NewsletterStep.STEP_3)
    }

    return (
        <>
            {isLoading && <Loader />}

            <Stack {...styles.wrapperNewsletter} data-testid="newsletter">
                {/* Step 1 */}
                {step === NewsletterStep.STEP_1 && (
                    <NewsletterRegistrationStep1
                        title={bannerNewsletter?.title_text}
                        description={bannerNewsletter?.title_description}
                        ctaText={bannerNewsletter?.cta_text}
                        localeID={locale.alias}
                        onSubmit={handleStep1Submit}
                        onLoading={(loader) => setLoading(loader)}
                        emailFromFooter={emailInputFooter}
                    />
                )}

                {/* Step 2 */}
                {step === NewsletterStep.STEP_2 && (
                    <NewsletterRegistrationStep2
                        title={titleStep2}
                        email={email}
                        localeID={locale.alias}
                        variant={isMobile ? 'mobile' : undefined}
                        description={bannerNewsletter?.title_description}
                        onSubmit={handleStep2Submit}
                        onLoading={(loader) => setLoading(loader)}
                    />
                )}

                {/* Step 3 */}
                {step === NewsletterStep.STEP_3 && (
                    <NewsletterRegistrationStep3
                        title={titleStep3}
                        isMobile={isMobile}
                        localeID={locale.alias}
                        mobileImage={bannerNewsletter?.image_url_mobile}
                        analyticsLabel={analyticsLabel}
                    />
                )}
            </Stack>
        </>
    )
}

type NewsletterImage = {
    url?: string
    alt?: string
}

type NewsletterPageDesignerData = {
    class?: string
    image_url_desktop?: NewsletterImage
    image_url_tablet?: NewsletterImage
    image_url_mobile?: NewsletterImage
    cta_text?: string
    title_description?: string
    title_text?: string
}

/**
 * Maps the page designer data.
 *
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Object} An object containing the footer columns, footer copyright, and footer socials.
 * @returns {Array<FooterColumn>} columns - The footer columns.
 * @returns {FooterCopyright} [legal] - The footer copyright.
 * @returns {FooterSocials} [socials] - The footer socials.
 */
const mapPageDesignerData = (pageComponents?: Page) => {
    const bannerNewsletterData: NewsletterPageDesignerData = {}

    const components = pageComponents?.regions?.[0]?.components

    if (components) {
        const bannerNewsletter = components[0].data

        bannerNewsletterData.class = bannerNewsletter?.class_name

        bannerNewsletterData.image_url_desktop = {
            url: bannerNewsletter?.cld_image_desktop?.imageUrl || bannerNewsletter?.image_desktop?.url || null,
            alt: bannerNewsletter?.cld_image_desktop?.alt || '',
        }

        bannerNewsletterData.image_url_tablet = {
            url: bannerNewsletter?.cld_image_tablet?.imageUrl || bannerNewsletter?.image_tablet?.url || null,
            alt: bannerNewsletter?.cld_image_tablet?.alt || '',
        }

        bannerNewsletterData.image_url_mobile = {
            url:
                bannerNewsletter?.cld_image_mobile?.imageUrl ||
                bannerNewsletter?.image_mobile?.url ||
                bannerNewsletter?.cld_image_desktop?.imageUrl ||
                bannerNewsletter?.image_desktop?.url ||
                null,
            alt: bannerNewsletter?.cld_image_mobile?.alt || bannerNewsletter?.cld_image_desktop?.alt || '',
        }

        bannerNewsletterData.cta_text = bannerNewsletter?.cta_text

        bannerNewsletterData.title_description = bannerNewsletter?.title_description

        bannerNewsletterData.title_text = bannerNewsletter?.title_text
    }

    return {
        bannerNewsletter: bannerNewsletterData,
    }
}

export {NewsletterModal, NewsletterStep, NewsletterRegistrationStep1, NewsletterRegistrationStep2, NewsletterRegistrationStep3}
